import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { GripVertical } from 'lucide-react'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Project,
  SummaryConfig,
  dashboardSummaries,
  getProjects,
  getSummaryConfigs,
  getSummaryOrder,
  orderSummaries,
  setSummaryOrder,
} from '../../api/client'
import AddSummaryButton from './AddSummaryButton'
import FeatureSummaryCard from './FeatureSummaryCard'
import { isRenderableKind } from './viewRegistry'

/**
 * DashboardSummaries — the dashboard's summaries section. The dashboard is the
 * one surface with explicit pinning: it lists every config pinned via
 * `pin_surfaces.dashboard` (see `dashboardSummaries`) and lets the user create a
 * new one for any project through the shared editor (`surface="dashboard"`).
 *
 * Owns its own fetch of configs + projects so the host page stays a thin shell;
 * a create / edit / delete re-fetches via `reload`.
 *
 * Cards are draggable into any order, persisted per user (see the summary-order
 * note in `user_prefs.py`). Two details the drag has to get right:
 *
 *  - It's a HANDLE, not a draggable card. These cards are full of interactive
 *    parts — the view switcher, the period pager, the gear, and streak strips
 *    that scroll horizontally under a finger — so arming the whole card would
 *    fight all of them, the strip's touch scroll worst of all. The handle is
 *    passed INTO the card (`dragHandle`) to sit with those other controls.
 *  - The add tile stays OUTSIDE `SortableContext`, so it isn't a drop target and
 *    always trails the cards.
 *
 * The grid is 2-up at `lg`, so this uses `rectSortingStrategy` rather than the
 * vertical-list strategy the collection modals use.
 */

/** One draggable grid cell. Renders via a child function so the handle can be
 *  planted INSIDE the card, in its corner-control cluster — dnd-kit's listeners
 *  have to ride the element the user actually grabs, and that's also what keeps
 *  keyboard sorting working (tab to the handle, space, arrows). */
const SortableCard: React.FC<{
  id: number
  children: (handle: React.ReactNode) => React.ReactNode
}> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id })

  const handle = (
    <button
      type="button"
      aria-label="Reorder summary"
      title="Drag to reorder"
      {...attributes}
      {...listeners}
      // touch-none so a drag from the handle isn't stolen by the page's scroll.
      className="cursor-grab touch-none rounded-lg p-2 text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-500 active:cursor-grabbing"
    >
      <GripVertical className="h-5 w-5" />
    </button>
  )

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        // Lift the dragged card over its neighbours as they shuffle under it.
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.85 : undefined,
      }}
      data-testid="dashboard-summary-slot"
      data-config-id={id}
    >
      {children(handle)}
    </div>
  )
}

const DashboardSummaries: React.FC = () => {
  const [configs, setConfigs] = useState<SummaryConfig[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  // The user's card order. `null` until fetched — the grid waits for it rather
  // than painting in id order and reshuffling a beat later.
  const [order, setOrder] = useState<number[] | null>(null)
  const [configsLoaded, setConfigsLoaded] = useState(false)

  const reload = useCallback(() => {
    getSummaryConfigs()
      .then(setConfigs)
      .catch((e) => console.error('Failed to load summaries', e))
      .finally(() => setConfigsLoaded(true))
  }, [])

  useEffect(() => {
    reload()
    // ALL statuses, not just active: the editor offers archived / completed
    // projects as a source too (a finished project is exactly what you want a
    // retrospective summary on), and this list seeds its picker.
    getProjects()
      .then(setProjects)
      .catch((e) => console.error('Failed to load projects for summaries', e))
    getSummaryOrder()
      .then((res) => setOrder(res.orders.dashboard ?? []))
      // An unreadable order is not worth losing the dashboard over — fall back
      // to the natural order.
      .catch(() => setOrder([]))
  }, [reload])

  // `isRenderableKind` drops configs this build has no view registry entry for
  // (a kind added by a newer build, or written straight to the shared DB by a
  // backfill). Drawing one throws, and a throw here unmounts the whole grid —
  // so an unknown kind costs its own card rather than every card.
  const pinned = useMemo(
    () =>
      orderSummaries(
        dashboardSummaries(configs).filter((c) => isRenderableKind(c.kind)),
        order ?? undefined,
      ),
    [configs, order],
  )

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = pinned.findIndex((c) => c.id === active.id)
    const newIndex = pinned.findIndex((c) => c.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return

    // Optimistic: the card is already under the cursor where it was dropped, so
    // the local order leads and the write follows. The whole list goes up, which
    // is what keeps the stored order internally consistent.
    const next = arrayMove(pinned, oldIndex, newIndex).map((c) => c.id)
    const previous = order
    setOrder(next)
    setSummaryOrder('dashboard', next).catch((e) => {
      console.error('Failed to save summary order', e)
      setOrder(previous)
    })
  }

  // Both the configs and the order have to land before the first paint, or the
  // cards visibly reshuffle once the order arrives.
  const ready = configsLoaded && order !== null

  return (
    <div>
      {ready && pinned.length === 0 && (
        <p className="mb-3 text-sm text-gray-400">
          No summaries pinned to the dashboard yet.
        </p>
      )}

      {/* The add tile trails the cards (last grid cell), so the first summary
          starts at the top of the section. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {ready && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={pinned.map((c) => c.id)}
              strategy={rectSortingStrategy}
            >
              {pinned.map((config) => (
                <SortableCard key={config.id} id={config.id}>
                  {(handle) => (
                    <FeatureSummaryCard
                      config={config}
                      surface="dashboard"
                      projects={projects}
                      onChanged={reload}
                      dragHandle={handle}
                    />
                  )}
                </SortableCard>
              ))}
            </SortableContext>
          </DndContext>
        )}
        <AddSummaryButton surface="dashboard" projects={projects} onChanged={reload} variant="tile" />
      </div>
    </div>
  )
}

export default DashboardSummaries
